
import { Component, Vue, Watch } from "vue-property-decorator";
import {
  CFormField,
  setFormFieldsDisabled,
  setFormFieldsAbleBodied,
  validateFormFields,
  TInput,
} from "@/models/form_cards";
import { ICheckToken } from "@/models/validation";
import router from "@/router";
import { SMApp, SMUser } from "@/store";
import { capitalizeString } from "@/utils/methods";
import { API_ROUTES } from "@/utils/API";
import { clearAuthStorage } from "@/utils/authStorage";

import { LINKS } from "@/constants/links";
import { EMPLOYEE_ROLE, EMPLOYER_ROLE, CANDIDATE_ROLE } from "@/constants/roles";

const LOGIN_ROUTE = "Login";

const ICONS = {
  person: "person-outline",
};

const AUTOCOMPLETE = {
  givenName: "given-name",
  familyName: "family-name",
  email: "email",
};

const MASKS = {
  phoneEmployer: "0NNNNNNNNNNNNNNNNN",
  phone: "NNNNNNNNNNNNNNNNNN",
};

const TYPES = {
  submit: "submit",
};

const INVITE_STATUSES = {
  used: "used",
  deleted: "deleted",
  expired: "expired",
  ok: "ok",
  error: "error",
};

type TRegistrationReqData = {
  first_name: string;
  last_name: string;
  title: string;
  email: string;
  phone: string;
  job: string;
  password_1: string;
  password_2: string;
  role: string;
  prepaid_sl: boolean;
};

const CASES_ROUTE = "/cases";
@Component({
  components: {
    FormCard: () => import("@/components/FormCard/FormCard.vue"),
    PasswordRequirements: () =>
      import("@/components/PasswordRequirements/PasswordRequirements.vue"),
  },
})
export default class Registration extends Vue {
  $refs!: {
    email: TInput;
  };

  links = LINKS;

  autocomplete = AUTOCOMPLETE;

  masks = MASKS;

  types = TYPES;

  inviteStatuses = INVITE_STATUSES;

  isInviteSend = false;

  inviteStatus = this.inviteStatuses.ok;

  get phoneMask() {
    if (this.isRoleNotEmployer) return this.masks.phone;

    return this.masks.phoneEmployer;
  }

  get errorTitle() {
    if (this.inviteStatus === this.inviteStatuses.expired) return this.$t("popup.expiredTitle");

    if (this.inviteStatus === this.inviteStatuses.error) return this.$t("popup.error");

    return this.$t("popup.deletedTitle");
  }

  get showSupportMessage() {
    return (
      this.inviteStatus === this.inviteStatuses.expired ||
      this.inviteStatus === this.inviteStatuses.error
    );
  }

  get showLoginMessage() {
    return [
      this.inviteStatuses.error,
      this.inviteStatuses.deleted,
      this.inviteStatuses.used,
    ].includes(this.inviteStatus);
  }

  get radioClasses() {
    return {
      disabled: this.role.totallyDisabled,
    };
  }

  options = [
    { value: EMPLOYER_ROLE, label: this.$t("roles.label.employer") },
    { value: EMPLOYEE_ROLE, label: this.$t("roles.label.employee") },
  ];

  titleOptions = [
    { value: this.$t("titles.mr"), label: this.$t("titles.mr") },
    { value: this.$t("titles.mrs"), label: this.$t("titles.mrs") },
    { value: this.$t("titles.miss"), label: this.$t("titles.miss") },
    { value: this.$t("titles.ms"), label: this.$t("titles.ms") },
    { value: this.$t("titles.doctor"), label: this.$t("titles.doctor") },
    { value: this.$t("titles.reverend"), label: this.$t("titles.reverend") },
  ];

  get isRoleEmployeeSelectable() {
    return this.role.value !== EMPLOYEE_ROLE || this.isInviteSend;
  }

  get isRoleEmployer() {
    return this.role.value === EMPLOYER_ROLE || !this.role.value;
  }

  get isRoleNotEmployer() {
    return this.role.value === EMPLOYEE_ROLE || this.role.value === CANDIDATE_ROLE;
  }

  get isInviteStatusOk() {
    return this.inviteStatus === this.inviteStatuses.ok;
  }

  icons = ICONS;

  get inputs() {
    const { email } = this.$refs;

    return [email];
  }

  showEachInputError() {
    this.inputs.forEach((input) => input.showError());
  }

  checkStringToTransform(string: string): boolean {
    let bool = false;
    const words = string.split(" ");

    words.forEach((word) => {
      word.split("").forEach((char, index) => {
        const checkEqual = char.charAt(index) === char.charAt(index).toUpperCase();

        if ((index === 0 && !checkEqual) || (index !== 0 && checkEqual)) bool = true;
      });
    });

    return bool;
  }

  transformString(string: string) {
    let newString = "";

    const words = string.split(" ");

    words.forEach((word, index) => {
      newString += capitalizeString(word.toLowerCase());

      if (words.length !== index + 1) newString += " ";
    });

    return newString;
  }

  @Watch("givenName.value")
  givenNameChanged(string: string, oldString: string) {
    if (string.trim() === oldString.trim()) return;

    if (!this.checkStringToTransform(string)) return;

    this.givenName.value = this.transformString(string);
  }

  @Watch("familyName.value")
  familyNameChanged(string: string, oldString: string) {
    if (string.trim() === oldString.trim()) return;

    if (!this.checkStringToTransform(string)) return;

    this.familyName.value = this.transformString(string);
  }

  @Watch("email.value")
  emailChanged() {
    this.state.isEmailErrorShown = false;
  }

  givenName = new CFormField("Given name").addRuleByAlias("name");
  familyName = new CFormField("Family name").addRuleByAlias("name");
  title = new CFormField("");
  email = new CFormField("Email")
    .addRuleByAlias("email")
    .addRule(() => !this.state.isEmailErrorShown || this.state.errorMessage);
  phone = new CFormField("Phone number");
  job = new CFormField("Job position");
  password1 = new CFormField("Password").addRuleByAlias("password");
  password2 = new CFormField("Confirm password").addRule(
    () => this.doPasswordsMatch || "Password doesn't match"
  );
  role = new CFormField("");

  isReadAndAccepted = false;

  isReadAndAcceptedToggle() {
    this.isReadAndAccepted = !this.isReadAndAccepted;
  }

  get doPasswordsMatch() {
    return this.password1.value === this.password2.value;
  }

  get fields() {
    const { givenName, familyName, title, email, phone, job, password1, password2, role } = this;

    const payload = [givenName, familyName, title, email, phone, password1, password2, role];

    if (this.isRoleEmployer) payload.push(job);

    return payload;
  }

  get isFormValid() {
    const { fields, isReadAndAccepted, isRoleEmployeeSelectable } = this;
    const isEachFieldValid = validateFormFields(fields);

    return !(!isEachFieldValid || !isReadAndAccepted || !isRoleEmployeeSelectable);
  }

  state = {
    isLoading: false,
    isStartLoading: false,
    isEmailErrorShown: false,
    isInstructionSent: false,
    isSuccessfullyRegistered: false,
    errorMessage: "",
  };

  @Watch("state.isLoading")
  isLoadingChanged() {
    if (!this.state.isLoading) return setFormFieldsAbleBodied(this.fields);

    setFormFieldsDisabled(this.fields);

    this.showEachInputError();
  }

  getData(): TRegistrationReqData {
    const {
      givenName,
      familyName,
      title,
      email,
      phone,
      job,
      password1,
      password2,
      role,
      prepaidSL,
    } = this;

    return {
      first_name: givenName.value,
      last_name: familyName.value,
      title: title.value,
      email: email.value.trim().toLowerCase(),
      phone: phone.value,
      job: job.value,
      password_1: password1.value,
      password_2: password2.value,
      role: role.value,
      prepaid_sl: prepaidSL,
    };
  }

  handleRegistrationError({ error }: { error: string }) {
    this.state.isEmailErrorShown = true;

    this.state.errorMessage = error;
  }

  async register() {
    try {
      this.state.isLoading = true;

      const data = this.getData();
      const params: { token?: string } = {};

      if (this.invitationToken) params.token = this.invitationToken;

      await this.$auth.register({ data, params, redirect: false });

      if (this.invitationToken) {
        this.state.isSuccessfullyRegistered = true;
      } else {
        this.state.isInstructionSent = true;
      }
    } catch (err) {
      if (!err?.response?.data) return;

      this.handleRegistrationError(err.response.data);
    } finally {
      this.state.isLoading = false;
    }
  }

  invitationToken: string | null = null;

  lastProcessedInviteToken: string | null = null;

  get routeQuery() {
    return this.$route.query;
  }

  @Watch("routeQuery", { immediate: true, deep: true })
  async routeQueryChanged(query: any) {
    if (!query) return;

    this.checkIsPrepaidSL(query);

    await this.checkInvitation(query);
  }

  prepaidSL = false;

  checkIsPrepaidSL({ prepaid_sl }: { prepaid_sl: string }) {
    if (prepaid_sl !== "true") return;

    this.prepaidSL = true;
  }

  async checkInvitation({ token, email, role }: { token: string; email: string; role: string }) {
    if (!token || !email || !role) return;

    if (this.state.isSuccessfullyRegistered) return;

    if (this.lastProcessedInviteToken !== token) {
      clearAuthStorage();
      this.lastProcessedInviteToken = token as string;
    }

    try {
      const { data } = await this.axios.get<ICheckToken>(
        API_ROUTES.COMPANY_CHECK_INVITE(token as string)
      );

      this.inviteStatus = data.invite_status;

      if (!this.isInviteStatusOk) return;

      this.isInviteSend = true;
      this.invitationToken = token as string;
      this.email.value = email as string;
      this.role.value = (role as string) === CANDIDATE_ROLE ? EMPLOYEE_ROLE : (role as string);

      this.email.setTotallyDisabled();
      this.role.setTotallyDisabled();
    } catch {
      this.inviteStatus = this.inviteStatuses.error;
    } finally {
      this.state.isLoading = false;

      // this.$router.push({ name: "Registration" });
    }
  }

  async start() {
    try {
      this.state.isStartLoading = true;

      const data = {
        email: this.email.value,
        password: this.password1.value,
      };

      await this.$auth.login({ data });

      await SMUser.updateCurrentUser();

      if (!SMUser.user) {
        throw new Error("Unable to load your account. Please try again.");
      }

      SMApp.checkAuthOnServer();

      await this.$nextTick();

      const path = SMUser.isCandidate ? CASES_ROUTE : SMUser.basePath;

      await router.push(path);
    } catch (err) {
      const error = err as {
        response?: { data?: { error?: string; message?: string } };
        message?: string;
      };

      this.state.isSuccessfullyRegistered = false;
      this.state.isEmailErrorShown = true;
      this.state.errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to sign in. Please try again.";
    } finally {
      this.state.isStartLoading = false;
    }
  }

  async logout() {
    await this.$router.push({
      name: LOGIN_ROUTE,
    });

    // TODO: fix (probably caused by keeping this component alive)
    // window.location.reload();
  }
}
